import { useId } from 'react';
import { Link } from 'react-router-dom';
import { RepeatIcon, CheckIcon } from 'lucide-react';
import { usePages } from '../../contexts/SiteContext';
import { useFeatures } from '../../hooks/useFeatures';
import { sectionHeading } from '../../ui/sections';
import { btnOnClay, btnPrimaryLg } from '../../ui/styles';
type Tone = 'clay' | 'shell';

/**
 * Uitleg van het abonnement met de voordelen. `clay` = het ene diepe vlak van de webshop;
 * `shell` = licht, voor pagina's die hun klei al elders gebruiken (Protocollen).
 * Op klei alleen `text-shell` (5,6:1); blush alleen voor iconen.
 */
export function SubscriptionBlock({
  tone = 'clay'
}: {
  tone?: Tone;
}) {
  const deep = tone === 'clay';
  const {
    subscription
  } = usePages();
  const headingId = useId();
  // Abonnement uitgezet (Instellingen › Koppelingen & privacy › Onderdelen): het blok staat nergens.
  const {
    subscriptions
  } = useFeatures();
  if (!subscriptions) return null;
  return <section className={`py-20 sm:py-24 lg:py-32 ${deep ? 'bg-clay text-shell' : 'bg-shell'}`} aria-labelledby={headingId} data-id="element-8788" data-mp="t519cy1wgd7p">
      <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8789" data-mp="jmv7kvzese1g">
        <div className="lg:col-span-5" data-id="element-8790" data-mp="5n3oigu1c7gl">
          <p className={`flex items-center gap-2 text-[14px] ${deep ? 'text-shell' : 'text-clay'}`} data-id="element-8791" data-mp="5o57d7haly8v">
            <RepeatIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8792" data-mp="9pgpyt1r1j7k" />
            {subscription.name}
          </p>
          <h2 id={headingId} className={`mt-5 ${sectionHeading} ${deep ? '' : 'text-ink'}`} data-id="element-8793" data-mp="xgcr4gs3hqrh">
            {subscription.claim}
          </h2>
          <p className={`mt-6 max-w-md text-[17px] leading-relaxed ${deep ? 'text-shell' : 'text-muted'}`} data-id="element-8794" data-mp="omhk137exq97">{subscription.intro}</p>
          <p className={`mt-5 text-[14px] ${deep ? 'text-shell' : 'text-muted'}`} data-id="element-8795" data-mp="sm8hfprg34mp">
            {subscription.price} · {subscription.smallPrint}
          </p>
          <Link to="/webshop" className={`mt-9 ${deep ? btnOnClay : btnPrimaryLg}`} data-id="element-8796" data-mp="1l3lylzcwqh98">
            Stel je abonnement samen
          </Link>
        </div>

        <ul className={`grid self-center sm:grid-cols-2 lg:col-span-7 ${deep ? 'border-t border-shell/30' : 'border-t border-line'}`} data-id="element-8797" data-mp="1uzb1kx1ycedn">
          {subscription.perks.map(perk => <li key={perk.title} className={`border-b py-7 sm:pr-8 ${deep ? 'border-shell/30' : 'border-line'}`} data-id="element-8798" data-mp="1lxuyzbhdpgjz">
              <h3 className={`flex items-start gap-2.5 font-display text-xl font-light leading-snug ${deep ? 'text-shell' : 'text-ink'}`} data-id="element-8799" data-mp="1gzjt1bu7fq71">
                <CheckIcon className={`mt-1 h-4 w-4 shrink-0 ${deep ? 'text-blush' : 'text-clay'}`} strokeWidth={2} aria-hidden="true" data-id="element-8800" data-mp="17aib3g43myqf" />
                {perk.title}
              </h3>
              <p className={`mt-2 pl-[1.625rem] text-[15px] leading-relaxed ${deep ? 'text-shell' : 'text-muted'}`} data-id="element-8801" data-mp="136aih7uisymx">{perk.body}</p>
            </li>)}
        </ul>
      </div>
    </section>;
}